import { FC, useState, useEffect } from "react"
import { BBColumnDef, PricingProfileEntity } from "../../../../types"
import Table from "../../table"
import MenuCell from "./cells/menu"
import ManageProfile from "../manage"
import BBDialog from "../../dialog"
import { useToast } from "../../../hooks"
import { useDeletePricingProfileMutation } from "../../../../redux/queries"

interface Props {
  data: PricingProfileEntity[]
  isLoading: boolean
  brandId: string
}

const ProfilesManageTable: FC<Props> = ({ data, isLoading, brandId }) => {
  const { pushSuccessToast, pushErrorToast } = useToast()

  const [selectedProfile, setSelectedProfile] = useState<
    PricingProfileEntity | undefined
  >()
  const [profileIdToDelete, setProfileIdToDelete] = useState<string>("")

  const [deleteProfile, { isSuccess, isError, error, reset }] =
    useDeletePricingProfileMutation()

  const clearDelete = () => {
    setProfileIdToDelete("")
    reset()
  }

  const handleDelete = () => {
    if (profileIdToDelete) {
      deleteProfile(profileIdToDelete)
    }
  }

  useEffect(() => {
    if (isSuccess && profileIdToDelete) {
      pushSuccessToast("Profile deleted")
      clearDelete()
    }
  }, [isSuccess, profileIdToDelete, pushSuccessToast])

  useEffect(() => {
    if (isError) {
      const errorMsg =
        (error as any)?.data?.message ?? "Failed to delete profile"
      pushErrorToast(errorMsg)
      clearDelete()
    }
  }, [isError, error, pushErrorToast])

  const columns: BBColumnDef<PricingProfileEntity>[] = [
    {
      id: "title",
      header: "Title",
      accessorFn: row => row.title,
      footer: props => props.column.id,
      cell: props => {
        const pref = props.row.original.profile_preference_type
        return (
          <td className="py-4 pl-4 pr-3 text-sm sm:pl-6 min-w-[180px]">
            <div className="font-medium text-gray-900">
              {props.row.original.title}
            </div>
            <div className="text-gray-500 text-xs mt-0.5 md:hidden">
              {props.row.original.type} &middot;{" "}
              {props.row.original.amortization_type}
            </div>
            {pref && (
              <div className="text-xs text-primary-400 mt-0.5">
                {pref.replace(/_/g, " ")}
              </div>
            )}
            {props.row.original.label && (
              <div className="text-gray-500 text-xs mt-0.5">
                {props.row.original.label}
              </div>
            )}
          </td>
        )
      }
    },
    {
      id: "type",
      header: "Type",
      accessorFn: row => row.type,
      footer: props => props.column.id,
      meta: { headerClassName: "hidden md:table-cell" },
      cell: props => (
        <td className="hidden md:table-cell whitespace-nowrap py-4 px-3 text-sm text-gray-600">
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {props.row.original.type}
          </span>
        </td>
      )
    },
    {
      id: "amortization_type",
      header: "Amortization",
      accessorFn: row => row.amortization_type,
      footer: props => props.column.id,
      meta: { headerClassName: "hidden lg:table-cell" },
      cell: props => (
        <td className="hidden lg:table-cell whitespace-nowrap py-4 px-3 text-sm text-gray-600">
          {props.row.original.amortization_type}
        </td>
      )
    },
    {
      id: "term",
      header: "Term",
      accessorFn: row => row.term,
      footer: props => props.column.id,
      meta: { headerClassName: "hidden lg:table-cell" },
      cell: props => {
        const months = Number(props.row.original.term)
        const years = months / 12
        return (
          <td className="hidden lg:table-cell whitespace-nowrap py-4 px-3 text-sm text-gray-600">
            {Number.isInteger(years) ? `${years}yr` : `${months}mo`}
          </td>
        )
      }
    },
    {
      id: "dp_percent",
      header: "Down %",
      accessorFn: row => row.dp_percent,
      footer: props => props.column.id,
      cell: props => (
        <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-600">
          {props.row.original.dp_percent}%
        </td>
      )
    },
    {
      id: "min_credit",
      header: "Credit",
      accessorFn: row => row.min_credit,
      footer: props => props.column.id,
      cell: props => (
        <td className="whitespace-nowrap py-4 px-3 text-sm text-gray-600">
          {props.row.original.min_credit}
        </td>
      )
    },
    {
      id: "edit",
      header: "",
      footer: props => props.column.id,
      cell: props => (
        <MenuCell
          info={props.row.original}
          onEdit={setSelectedProfile}
          onDelete={setProfileIdToDelete}
        />
      )
    }
  ]

  return (
    <div className="pt-4">
      <Table
        data={data}
        columns={columns}
        viewType="table"
        tableClassName="table-auto"
        dropdownFilterColumnId="type"
        autoResetPageIndex={false}
        searchPlaceholder="Search profiles"
        shrinkFilterSort
        isLoading={isLoading}
      />
      <ManageProfile
        open={!!selectedProfile}
        data={selectedProfile}
        brandId={brandId}
        onClose={() => setSelectedProfile(undefined)}
      />
      <BBDialog
        open={!!profileIdToDelete}
        onClose={clearDelete}
        title="Delete profile"
        body="This will permanently delete this pricing profile. If it is currently used as a program preference, deletion will fail. Are you sure?"
        okLabel="Yes, Delete"
        cancelLabel="Cancel"
        onOk={handleDelete}
        onCancel={clearDelete}
      />
    </div>
  )
}

export default ProfilesManageTable
